import { atom } from 'nanostores';

import { $anchorTick, hasAnchor } from './anchors';
import { $hintProgress, dismissHint as markDismissed } from './progress';
import type { Hint, HintId } from './types';

/** All hints known to the engine. Insertion order is the priority tiebreaker. */
export const $hintRegistry = atom<readonly Hint[]>([]);

/** The hint currently being shown, or `null`. */
export const $activeHint = atom<HintId | null>(null);

export function registerHint(hint: Hint): void {
  const current = $hintRegistry.get();
  const existingIndex = current.findIndex((h) => h.id === hint.id);
  if (existingIndex === -1) {
    $hintRegistry.set([...current, hint]);
    return;
  }
  if (import.meta.env.DEV) {
    console.warn(`Hint '${hint.id}' is already registered; replacing.`);
  }
  const next = current.slice();
  next[existingIndex] = hint;
  $hintRegistry.set(next);
}

export function unregisterHint(id: HintId): void {
  const current = $hintRegistry.get();
  const next = current.filter((h) => h.id !== id);
  if (next.length !== current.length) $hintRegistry.set(next);
  if ($activeHint.get() === id) $activeHint.set(null);
}

/**
 * Mark the active hint dismissed. The progress write triggers `reevaluate`,
 * which transitions `$activeHint` directly to the next eligible hint (or
 * null) in a single store write. No-op when nothing is active.
 */
export function dismissActiveHint(): void {
  const id = $activeHint.get();
  if (id === null) return;
  const hint = findHint(id);
  if (hint !== null) markDismissed(hint.id, hint.version);
}

function findHint(id: HintId): Hint | null {
  return $hintRegistry.get().find((h) => h.id === id) ?? null;
}

function isEligible(hint: Hint): boolean {
  if (!hasAnchor(hint.anchor)) return false;
  const entry = $hintProgress.get()[hint.id];
  if (entry !== undefined && entry.version >= hint.version) return false;
  return true;
}

function pickNext(): Hint | null {
  const candidates = $hintRegistry
    .get()
    .map((hint, registryIndex) => ({ hint, registryIndex }))
    .filter(({ hint }) => isEligible(hint));

  if (candidates.length === 0) return null;

  candidates.sort((a, b) => {
    if (a.hint.priority !== b.hint.priority) return b.hint.priority - a.hint.priority;
    return a.registryIndex - b.registryIndex;
  });

  return candidates[0]?.hint ?? null;
}

/**
 * Picks the next eligible hint and assigns `$activeHint` in a single write.
 * Avoids a `null → next` two-step that would otherwise produce a blank frame
 * between hints under React's batching of nanostore notifications.
 */
function reevaluate(): void {
  const activeId = $activeHint.get();
  if (activeId !== null) {
    const hint = findHint(activeId);
    if (hint !== null && isEligible(hint)) return;
  }

  const nextId = pickNext()?.id ?? null;
  if (nextId !== activeId) $activeHint.set(nextId);
}

$hintRegistry.subscribe(reevaluate);
$anchorTick.subscribe(reevaluate);
$hintProgress.subscribe(reevaluate);
